import { z } from 'zod';

const TipoFluxoMovimentacaoEnum = z.enum(['envio', 'devolucao']);

const StatusMovimentacaoEnum = z.enum([
  'aguardando_preparacao',
  'aguardando_postagem_retirada',
  'em_transito',
  'aguardando_retirada_correios',
  'entregue',
  'cancelada',
]);

const MetodoEntregaEnum = z.enum(['correios', 'uber', 'pessoalmente', 'outro']);

const TipoEquipamentoEnum = z.enum([
  'imac',
  'macmini',
  'macbook',
  'monitor',
  'notebook',
  'periferico',
  'smartphone',
  'cartao_acesso',
  'chip',
  'outros',
]);

const SistemaOperacionalEnum = z.enum(['android', 'ios', 'linux_ubuntu', 'macos', 'windows', 'outros']);

const PropriedadeEquipamentoEnum = z.enum(['raro', 'cliente', 'nao_informado']);

const ProcessoMovimentacaoEnum = z.enum(['homologacao', 'remocao_imagem', 'devolucao_ao_cliente']);

const DestinoAposDevolucaoEnum = z.enum(['estoque_raro', 'direto_ao_cliente']);

const StatusEtapaProcessoEnum = z.enum(['pendente', 'concluido']);

/** Tipos de equipamento que exigem Sistema Operacional (Discovery §4.3 — dropdown real). */
const TIPOS_EQUIPAMENTO_COM_SO = ['notebook', 'macbook', 'macmini', 'imac', 'smartphone'] as const;

const MovimentacaoInformativoSchema = z.object({
  tipo: z.string().min(1),
  status: StatusEtapaProcessoEnum,
});

const MovimentacaoEtapasSchema = z.object({
  fotos: StatusEtapaProcessoEnum,
  termo: StatusEtapaProcessoEnum,
  informativos: z.array(MovimentacaoInformativoSchema),
});

/**
 * Metade "intake" de um item de equipamento (Movimentação pode ter mais de um, ex.
 * notebook + monitor no mesmo envio) — o que quem abre a Solicitação sabe informar.
 * Fica separada da metade "execução" (patrimônio/nº série/vínculo) em campos de
 * documento distintos de propósito: o Firestore Rules não consegue restringir chaves
 * dentro de elementos de um array, então só campos de documento diferentes garantem que
 * o solicitante não consiga incluir dados de execução na criação (ver firestore.rules).
 */
const MovimentacaoItemIntakeSchema = z.object({
  id: z.string().min(1),
  tipoEquipamento: TipoEquipamentoEnum.nullable(),
  sistemaOperacional: SistemaOperacionalEnum.nullable().optional(),
  homologado: z.boolean().nullable().optional(),
  propriedadeEquipamento: PropriedadeEquipamentoEnum.nullable().optional(),
  equipamentoSugeridoId: z.string().nullable().optional(),
  estadiaClienteOrigemId: z.string().nullable().optional(),
  descricaoLivre: z.string().nullable().optional(),
});

/** Metade "execução" — exclusiva do SI, nunca alcançável no `create` (ver firestore.rules). */
const MovimentacaoItemExecucaoSchema = z.object({
  id: z.string().min(1),
  equipamentoId: z.string().nullable().optional(),
  modelo: z.string().nullable().optional(),
  patrimonio: z.string().nullable().optional(),
  nSerie: z.string().nullable().optional(),
  senha: z.string().nullable().optional(),
  condicao: z.string().nullable().optional(),
  acessorios: z.array(z.string()).nullable().optional(),
  acessoriosOutros: z.string().nullable().optional(),
  observacao: z.string().nullable().optional(),
  glpiAtualizado: z.boolean().nullable().optional(),
  fotos: z.array(z.string()).nullable().optional(),
});

type MovimentacaoItemIntakeInput = z.infer<typeof MovimentacaoItemIntakeSchema>;

/**
 * Regra condicional de SO, aplicada por item. "Homologado" não entra aqui: sua
 * obrigatoriedade depende da config por Cliente (regraCliente.usaEquipamentoHomologado),
 * que este schema puro não tem como consultar — quem decide se o campo aparece e é
 * exigido é a tela (ver homologadoVisivel em NovaSolicitacaoContent/MovimentacaoCard).
 */
function validarItensIntake(itens: MovimentacaoItemIntakeInput[], ctx: z.RefinementCtx) {
  itens.forEach((item, index) => {
    if (
      item.tipoEquipamento != null &&
      TIPOS_EQUIPAMENTO_COM_SO.includes(item.tipoEquipamento as (typeof TIPOS_EQUIPAMENTO_COM_SO)[number]) &&
      !item.sistemaOperacional &&
      // Item de devolução (equipamentoSugeridoId presente) — o SO já é sabido pelo
      // equipamento real (si_equipamentos), não é uma escolha de quem abre a Solicitação;
      // exigir aqui bloquearia Mudança de Projeto/Troca de Equipamento/Devolução sempre
      // que o tipo derivado do equipamento real exigisse SO (ex. notebook).
      !item.equipamentoSugeridoId
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'O sistema operacional é obrigatório para este tipo de equipamento',
        path: [index, 'sistemaOperacional'],
      });
    }
  });
}

/**
 * Schema de criação. Quem preenche pode ser SI (Movimentações seguintes, telas de
 * execução) ou quem abre a Solicitação (só a primeira Movimentação, campos de intake —
 * ver firestore.rules). Por isso `dataPrevista`/`metodoEntrega` são opcionais aqui: podem
 * ficar ausentes até o SI completá-los depois (MovimentacaoUpdate). `equipamentosIntake`
 * pode vir vazio (ex. desligamento — SI completa depois). Nunca aceita
 * `equipamentosExecucao` aqui — essa metade só existe via update.
 */
export const MovimentacaoCreateSchema = z
  .object({
    tipoFluxo: TipoFluxoMovimentacaoEnum,
    equipamentosIntake: z.array(MovimentacaoItemIntakeSchema),
    clienteId: z.string().nullable().optional(),
    dataPrevista: z.any().nullable().optional(), // Timestamp do Firestore, aceito antes da conversão
    metodoEntrega: MetodoEntregaEnum.nullable().optional(),
    codigoRastreio: z.string().nullable().optional(),
    alocacaoReferenciadaId: z.string().nullable().optional(),
  })
  .refine(
    (data) => data.metodoEntrega !== 'correios' || Boolean(data.codigoRastreio),
    {
      message: 'Código de rastreio é obrigatório quando o método de entrega é Correios',
      path: ['codigoRastreio'],
    }
  )
  .superRefine((data, ctx) => validarItensIntake(data.equipamentosIntake, ctx));

export const MovimentacaoUpdateSchema = z.object({
  id: z.string().min(1, 'O ID da movimentação é obrigatório'),
  statusMovimentacao: StatusMovimentacaoEnum.optional(),
  clienteId: z.string().nullable().optional(),
  equipamentosIntake: z.array(MovimentacaoItemIntakeSchema).optional(),
  equipamentosExecucao: z.array(MovimentacaoItemExecucaoSchema).optional(),
  dataPrevista: z.any().nullable().optional(),
  dataEnvio: z.any().nullable().optional(),
  dataRecebimento: z.any().nullable().optional(),
  metodoEntrega: MetodoEntregaEnum.nullable().optional(),
  codigoRastreio: z.string().nullable().optional(),
  processo: ProcessoMovimentacaoEnum.nullable().optional(),
  destinoAposDevolucao: DestinoAposDevolucaoEnum.nullable().optional(),
  etapas: MovimentacaoEtapasSchema.partial().optional(),
  fotos: z.array(z.string()).nullable().optional(),
  termoUrl: z.string().nullable().optional(),
  alocacaoReferenciadaId: z.string().nullable().optional(),
  /** Força a function a reler o responsável da alocação (mudou na tela de Alocações). */
  alocacaoPrecisaSincronizar: z.boolean().optional(),
});

export const MovimentacaoSchema = z.object({
  id: z.string().min(1),
  tipoFluxo: TipoFluxoMovimentacaoEnum,
  statusMovimentacao: StatusMovimentacaoEnum,
  equipamentosIntake: z.array(MovimentacaoItemIntakeSchema),
  equipamentosExecucao: z.array(MovimentacaoItemExecucaoSchema).optional(),
  clienteId: z.string().nullable().optional(),
  dataPrevista: z.any().nullable().optional(),
  dataEnvio: z.any().nullable().optional(),
  dataRecebimento: z.any().nullable().optional(),
  metodoEntrega: MetodoEntregaEnum.nullable().optional(),
  codigoRastreio: z.string().nullable().optional(),
  processo: ProcessoMovimentacaoEnum.nullable().optional(),
  destinoAposDevolucao: DestinoAposDevolucaoEnum.nullable().optional(),
  etapas: MovimentacaoEtapasSchema,
  fotos: z.array(z.string()).nullable().optional(),
  termoUrl: z.string().nullable().optional(),
  createdBy: z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    avatar: z.string().nullable().optional(),
  }),
  updatedBy: z
    .object({
      id: z.string().min(1),
      name: z.string().min(1),
      avatar: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  createdAt: z.any(),
  updatedAt: z.any(),
});
